import { Controller } from "@hotwired/stimulus"
import pageMessageBroker from "../page_message_broker"

export default class extends Controller {
  static targets = ["field"]

  connect() {
    this.subscriptions = []
    this.setupSubscriptions()
    this.updateFieldStates()
  }

  disconnect() {
    // Clean up all subscriptions
    this.subscriptions.forEach(subscription => subscription.unsubscribe())
    this.subscriptions = []
  }

  setupSubscriptions() {
    // Only subscribe to field changes if there are fields with dependencies
    const hasDependencies = this.fieldTargets.some(fieldContainer => {
      const dependencies = JSON.parse(fieldContainer.dataset.dependencies || "{}")
      return Object.keys(dependencies).length > 0
    })

    if (hasDependencies) {
      const subscription = pageMessageBroker.subscribe("plan-design-field-changed", (event) => {
        this.handleFieldChange(event)
      })
      this.subscriptions.push(subscription)
    }
  }

  handleFieldChange(event) {
    // Update field states when any field changes
    this.updateFieldStates()
  }

  updateFieldStates() {
    this.fieldTargets.forEach(fieldContainer => {
      const dependencies = JSON.parse(fieldContainer.dataset.dependencies || "{}")
      const explicitDependenciesEnabled = this.checkDependencies(dependencies)
      const hasInsuranceEnabled = this.checkHasInsuranceDependency(fieldContainer)
      const isEnabled = explicitDependenciesEnabled && hasInsuranceEnabled
      this.setFieldEnabled(fieldContainer, isEnabled)
    })
  }

  checkDependencies(dependencies) {
    for (const [dependencyPath, expectedValue] of Object.entries(dependencies)) {
      const [sectionName, fieldName] = dependencyPath.split(".")
      const actualValue = this.getFieldValue(sectionName, fieldName)

      console.log(`Checking dependency for ${dependencyPath}: expected "${expectedValue}", actual "${actualValue}"`)
      
      if (actualValue !== expectedValue) {
        return false
      }
    }
    return true
  }

  checkHasInsuranceDependency(fieldContainer) {
    // Extract field information from the container
    const fieldPath = this.getFieldPathFromContainer(fieldContainer)
    if (!fieldPath) return true

    const [sectionName, fieldName] = fieldPath.split(".")
    
    // Skip has_insurance fields themselves
    if (fieldName === "has_insurance") {
      return true
    }

    // Check if there's a has_insurance field for this section
    const hasInsuranceValue = this.getFieldValue(sectionName, "has_insurance")
    
    if (hasInsuranceValue === null) {
      // No has_insurance field exists for this section, so enable the field
      return true
    }

    // Only enable if has_insurance is "Yes"
    return hasInsuranceValue === "Yes"
  }

  getFieldPathFromContainer(fieldContainer) {
    // Try to find an input/select element and extract the field path from its name
    const input = fieldContainer.querySelector("input, select, textarea")
    if (!input || !input.name) return null

    const matches = input.name.match(/plan_design\[fields\]\[([^\]]+)\]\[([^\]]+)\]/)
    if (matches) {
      const [, sectionName, fieldName] = matches
      return `${sectionName}.${fieldName}`
    }
    
    return null
  }

  getFieldValue(sectionName, fieldName) {
    const selector = `[name="plan_design[fields][${sectionName}][${fieldName}]"]`
    const field = document.querySelector(selector)
    return field ? field.value : null
  }

  setFieldEnabled(fieldContainer, isEnabled) {
    const inputs = fieldContainer.querySelectorAll("input, select, textarea")
    
    inputs.forEach(input => {
      input.disabled = !isEnabled
      
      if (isEnabled) {
        input.classList.remove("opacity-50", "cursor-not-allowed")
        fieldContainer.classList.remove("opacity-50")
      } else {
        input.classList.add("opacity-50", "cursor-not-allowed")
        fieldContainer.classList.add("opacity-50")
      }
    })
  }

  // This method will be called when a field value changes
  fieldChanged(event) {
    const fieldName = event.target.name
    const fieldValue = event.target.value
    
    if (fieldName && fieldName.includes("plan_design[fields]")) {
      // Extract section and field name from the field name attribute
      const matches = fieldName.match(/plan_design\[fields\]\[([^\]]+)\]\[([^\]]+)\]/)
      if (matches) {
        const [, sectionName, fieldNamePart] = matches
        
        // Publish the field change event
        pageMessageBroker.dispatch("plan-design-field-changed", {
          sectionName,
          fieldName: fieldNamePart,
          value: fieldValue,
          fullFieldName: fieldName
        })
      }
    }
  }
}
